import { useState, useEffect, useCallback } from 'react';
import { fetchWrapper } from '@/lib/fetch-wrapper';
import { urls } from '@/urls';
import { getUser } from '@/lib/common';
import { fetchAllSpecialPermissions } from '@/lib/permissions';

export interface RejectedResource {
  id: number;
  resourceName: string;
  resourceType: string;
  subscription: string;
  system: string;
  cost: number;
  resourceOwner: string;
  ownerId: number;
  dateRejected: string;
  reason: string;
  currency?: string;
  cloudAccountId?: number;
}

interface AttestationData {
  users: any[];
  systems: any[];
  departments: any[];
  cloudAccounts: any[];
  resources: any[];
  resourceGroups: any[];
  rejectedResources: RejectedResource[];
}

export const useAttestation = () => {
  const [attestationData, setAttestationData] = useState<AttestationData>({
    users: [],
    systems: [],
    departments: [],
    cloudAccounts: [],
    resources: [],
    resourceGroups: [],
    rejectedResources: [],
  });
  const [dataLoading, setDataLoading] = useState(true);
  const [dataError, setDataError] = useState<string | null>(null);
  const [permissions, setPermissions] = useState({
    isResourceOwner: false,
    isCloudAccountOwner: false,
    isDepartmentOwner: false,
    isSystemOwner: false,
  });

  const user = getUser();

  const refetchAttestationData = useCallback(async () => {
    if (!user?.id) return;

    setDataLoading(true);
    setDataError(null);

    try {
      const currency = user?.default_currency || 'USD';

      const [
        attestationDataResponse,
        allResourcesResponse,
        resourceGroupsResponse,
      ] = await Promise.all([
        fetchWrapper.get(urls.DevOps.attestationData + `?currency=${currency}`),
        fetchWrapper.get(
          urls.DevOps.getAttestationTable + `?currency=${currency}`,
        ),
        fetchWrapper.get(urls.DevOps.resourcegroups),
      ]);

      const processedRejectedResources =
        allResourcesResponse
          ?.filter((item: any) => item.Status === 'REJECTED')
          .map((item: any) => ({
            id: item.id,
            resourceName: item.resource_name || item.name,
            resourceType: item.resource_type || item.type,
            subscription: item.subscription_name || item.subscription,
            system: item.system_name || item.system,
            cost: item.cost || 0,
            resourceOwner: item.owner_name || item.resource_owner,
            ownerId: item.owner_id,
            dateRejected: item.last_updated,
            reason: item.reason,
            currency: item.currency || 'USD',
            cloudAccountId: item.cloud_account_id,
          })) || [];

      const allResources = allResourcesResponse || [];

      processedRejectedResources.sort((a: any, b: any) => b.cost - a.cost);

      setAttestationData({
        users: attestationDataResponse?.users || [],
        systems: attestationDataResponse?.systems || [],
        departments: attestationDataResponse?.departments || [],
        cloudAccounts: attestationDataResponse?.cloudAccounts || [],
        resources: allResources.sort((a: any, b: any) =>
          a.cost < b.cost ? 1 : -1,
        ),
        resourceGroups: resourceGroupsResponse || [],
        rejectedResources: processedRejectedResources,
      });
    } catch (err) {
      console.error('Error refetching attestation data:', err);
      setDataError('Failed to refetch attestation data');
    } finally {
      setDataLoading(false);
    }
  }, [user?.id, user?.default_currency]);

  useEffect(() => {
    const fetchPermissions = async () => {
      if (!user?.id) return;

      try {
        const userPermissions = await fetchAllSpecialPermissions(user.id);
        setPermissions(userPermissions);
      } catch (error) {
        console.error('Error fetching permissions:', error);
      }
    };

    fetchPermissions();
  }, [user?.id]);

  useEffect(() => {
    refetchAttestationData();
  }, [user?.id, user?.default_currency, refetchAttestationData]);

  const attestateResources = useCallback(async (resourceIds: number[]) => {
    try {
      await fetchWrapper.post(urls.DevOps.attestateResources, {
        resourceIds: resourceIds,
      });
      // Return success, parent component will handle refetch
      return true;
    } catch (error) {
      console.error('Error attesting resources:', error);
      throw error;
    }
  }, []);

  const rejectResources = useCallback(
    async (resourceIds: number[], reason?: string) => {
      try {
        await fetchWrapper.post(urls.DevOps.rejectResources, {
          resourceIds: resourceIds,
          reason: reason,
        });
        return true;
      } catch (error) {
        console.error('Error rejecting resources:', error);
        throw error;
      }
    },
    [],
  );

  return {
    data: attestationData,
    loading: dataLoading,
    error: dataError,
    permissions,

    attestateResources,
    rejectResources,

    refetch: refetchAttestationData,

    user,
  };
};
